import { ref, computed, watch } from 'vue';
import { betsTypeSwitch } from '@/common/sb-transaction.helpers';
import type { BetHistoryEntity } from '@/common/api/model';
import type { ExternalTransactionKind } from '@/common/bot/enum/ExternalTransactionType';
import { useBetTransactions as useBetTransactionsQuery } from '../api/betTransactions.api';

export interface UseBetTransactionsOptions {
  transactionSubtypes: ExternalTransactionKind[];
  pageSize?: number;
}

/**
 * Build comma-separated bet status codes from transaction subtypes
 */
function buildBetStatusCodes(transactionSubtypes: ExternalTransactionKind[]): string {
  return transactionSubtypes.map((transactionType) => betsTypeSwitch.match(transactionType)).join(',');
}

/**
 * Composable for managing bet transactions
 * Provides pagination, loading states, and data transformation
 */
export function useBetTransactions(options: UseBetTransactionsOptions) {
  const { transactionSubtypes, pageSize = 10 } = options;

  const currentPage = ref(1);
  /** Accumulates all loaded pages so "load more" shows a full list instead of only the latest page */
  const accumulatedEntities = ref<BetHistoryEntity[]>([]);

  // Derive bet status codes from subtypes
  const betSubTypeCodes = computed(() => buildBetStatusCodes(transactionSubtypes));

  // Query parameters for TanStack Query
  const queryParams = computed(() => ({
    ...(betSubTypeCodes.value ? { winningStatus: betSubTypeCodes.value } : { isSettled: 10 }),
    pageSize,
    pageNo: currentPage.value,
  }));

  // Fetch data using TanStack Query
  const { data, isLoading, error, refetch } = useBetTransactionsQuery(queryParams);

  // Keep accumulated list in sync with fetched pages (so load more shows full list)
  watch(
    [() => data.value, currentPage],
    ([response]) => {
      const entityList = (response as { entityList?: BetHistoryEntity[] } | undefined)?.entityList ?? [];
      if (entityList.length === 0) return;
      if (currentPage.value === 1) {
        accumulatedEntities.value = [...entityList];
      } else {
        const existingIds = new Set(accumulatedEntities.value.map((e) => e.shortId));
        const newEntities = entityList.filter((e) => !existingIds.has(e.shortId));
        if (newEntities.length > 0) {
          accumulatedEntities.value = [...accumulatedEntities.value, ...newEntities];
        }
      }
    },
    { immediate: true }
  );

  // Computed properties - return full BetHistoryEntity objects (accumulated across pages)
  const transactions = computed<BetHistoryEntity[]>(() => accumulatedEntities.value);
  const totalBets = computed(() => data.value?.totalNum || 0);
  const totalPages = computed(() => Math.ceil((data.value?.totalNum || 1) / pageSize));
  const hasMore = computed(() => currentPage.value < totalPages.value);

  // Actions
  const loadMore = () => {
    if (hasMore.value) {
      currentPage.value += 1;
    }
  };

  const refresh = () => {
    currentPage.value = 1;
    accumulatedEntities.value = [];
    refetch();
  };

  const goToPage = (page: number) => {
    if (page > 0 && page <= totalPages.value) {
      currentPage.value = page;
    }
  };

  return {
    // Full BetHistoryEntity objects (not transformed)
    transactions,
    loading: isLoading,
    error: error?.value?.message || null,
    totalBets,
    hasMore,

    // Actions
    loadMore,
    refresh,
    goToPage,
  };
}
